// frontend-dash/src/components/EditableField.js
import React, { useState, useRef, useEffect } from 'react';
import {
  EditIcon as Edit2,
  SaveIcon as Save,
  CloseIcon as X,
  CheckIcon as Check,
} from './Icons';

/**
 * EditableField — A wrapper that makes any content inline-editable.
 *
 * Usage:
 *   <EditableField
 *     value={event.title}
 *     onSave={(newValue) => handleSave('title', newValue)}
 *     type="text"         // 'text' | 'textarea' | 'date' | 'time' | 'select' | 'richtext'
 *     label="Titel"
 *     selectOptions={[]}  // only for type='select'
 *   >
 *     <h1>{event.title}</h1>   // The normal display
 *   </EditableField>
 */
const EditableField = ({
  value,
  onSave,
  type = 'text',
  label,
  placeholder,
  maxLength,
  rows = 4,
  selectOptions = [],
  children,
  className = '',
  disabled = false,
  onUpload,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (currentValue === value) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(currentValue);
      setIsEditing(false);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCurrentValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type !== 'textarea') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (disabled) {
    return <>{children}</>;
  }

  // Toggle type — saves immediately on click, no edit mode
  if (type === 'toggle') {
    const handleToggle = async () => {
      setIsSaving(true);
      try {
        await onSave(!value);
      } catch (err) {
        console.error('Toggle save failed:', err);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className={`flex items-center justify-between p-4 rounded-xl bg-surface-container transition-colors ${className}`}>
        <div className="flex-1 min-w-0">
          {children}
        </div>
        <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
          <input
            type="checkbox"
            checked={!!value}
            onChange={handleToggle}
            className="sr-only peer"
            disabled={disabled || isSaving}
          />
          <div className={`w-12 h-7 bg-outlineVariant rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:rounded-full after:h-[22px] after:w-[22px] after:transition-all peer-checked:after:translate-x-5 ${isSaving ? 'opacity-50' : ''}`}></div>
        </label>
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div
        className={`editable-field group relative cursor-pointer ${className}`}
        onClick={() => setIsEditing(true)}
        title={`${label || 'Feld'} bearbeiten`}
      >
        {children}
        <div className="editable-field-overlay">
          <Edit2 sx={{ fontSize: 16 }} />
        </div>
      </div>
    );
  }

  const inputClasses =
    'w-full bg-surface-containerLowest border-2 border-primary/40 rounded-xl px-4 py-3 text-surface-on focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium';

  return (
    <div
      className={`editable-field-active rounded-2xl p-4 bg-primary/5 border-2 border-primary/30 animate-fade-in ${className}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-label-small font-bold text-primary uppercase tracking-wider flex items-center gap-2">
          <Edit2 sx={{ fontSize: 14 }} />
          {label}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleCancel}
            className="p-1.5 rounded-lg hover:bg-surface-containerHigh text-surface-onVariant transition-colors"
            title="Abbrechen"
          >
            <X sx={{ fontSize: 18 }} />
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-on font-bold text-label-small flex items-center gap-1.5 hover:shadow-elevation-2 transition-all disabled:opacity-50"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-primary-on/30 border-t-primary-on rounded-full animate-spin" />
            ) : (
              <Check sx={{ fontSize: 16 }} />
            )}
            Speichern
          </button>
        </div>
      </div>

      {type === 'textarea' ? (
        <textarea
          ref={inputRef}
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`${inputClasses} resize-none`}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
        />
      ) : type === 'select' ? (
        <select
          ref={inputRef}
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          className={`${inputClasses} h-12`}
        >
          {selectOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : type === 'date' ? (
        <input
          ref={inputRef}
          type="date"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
        />
      ) : type === 'time' ? (
        <input
          ref={inputRef}
          type="time"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
        />
      ) : type === 'url' ? (
        <div className="space-y-2 w-full">
          <input
            ref={inputRef}
            type="url"
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={inputClasses}
            placeholder={placeholder || 'https://...'}
          />
          {onUpload && (
            <div className="flex items-center gap-2 mt-1">
              <label className="md3-btn-outlined text-label-small flex items-center gap-1.5 cursor-pointer py-1.5 px-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setIsSaving(true);
                    try {
                      const uploadedUrl = await onUpload(file);
                      if (uploadedUrl) {
                        setCurrentValue(uploadedUrl);
                      }
                    } catch (err) {
                      console.error('File upload failed in EditableField:', err);
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  className="hidden"
                />
                <span>Bild hochladen</span>
              </label>
            </div>
          )}
        </div>
      ) : type === 'number' ? (
        <input
          ref={inputRef}
          type="number"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
          placeholder={placeholder}
          min={0}
        />
      ) : type === 'color' ? (
        <div className="flex items-center gap-4">
          <input
            type="color"
            value={currentValue || '#cccccc'}
            onChange={(e) => setCurrentValue(e.target.value)}
            className="h-12 w-20 rounded-lg cursor-pointer border-0 p-0 bg-transparent"
          />
          <span className="font-mono font-bold text-surface-on bg-surface px-3 py-1 rounded border border-outlineVariant">
            {(currentValue || '#cccccc').toUpperCase()}
          </span>
        </div>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      )}

      {maxLength && (
        <div className="flex justify-end mt-1">
          <span className="text-[10px] font-medium text-surface-onVariant/60 italic">
            {(currentValue || '').length} / {maxLength} Zeichen
          </span>
        </div>
      )}
    </div>
  );
};

export default EditableField;
